// Reciprocal share-back profile picker. After a successful subscribe, the
// subscriber is asked WHICH of their own profiles to share back with the
// inviter (mutual connection). Public is preselected; dismissing defaults to
// Public (resolved server-side when no id is sent).
import { h } from './dom.js';
import type { Profile } from '../api/types.js';

// pickDefaultShareBackProfile picks the profile preselected in the picker:
// the (first enabled) PUBLIC profile, else the first enabled profile, else the
// first profile. Returns undefined for an empty list.
export function pickDefaultShareBackProfile(profiles: Profile[]): Profile | undefined {
  if (!profiles.length) return undefined;
  const enabled = profiles.filter((p) => p.enabled !== false);
  const pool = enabled.length ? enabled : profiles;
  return pool.find((p) => p.is_public) ?? pool[0];
}

export interface ShareBackResult {
  // The chosen profile id, or undefined to default to Public server-side
  // (dismiss / close / Esc / no non-default pick).
  profileId?: string;
}

// showShareBackModal renders the picker overlay and resolves once the user
// applies a choice or dismisses. Dismissal resolves { profileId: undefined }.
export function showShareBackModal(inviterName: string, profiles: Profile[]): Promise<ShareBackResult> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (r: ShareBackResult) => {
      if (done) return;
      done = true;
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(r);
    };

    const preselect = pickDefaultShareBackProfile(profiles);
    const options = profiles.map((p) => {
      const input = h('input', {
        type: 'radio', name: 'vid-shareback', value: p.id,
        style: 'margin-right:8px',
      }) as HTMLInputElement;
      if (preselect && p.id === preselect.id) input.checked = true;
      const label = h('label', {
        style: 'display:flex;align-items:center;padding:8px 10px;border:1px solid var(--border);border-radius:8px;cursor:pointer;margin-bottom:6px',
      }, input, h('span', {},
        p.name || 'Profile',
        p.is_public ? h('span', { class: 'badge badge-brand', style: 'margin-left:8px' }, 'Public') : null,
      ));
      return label;
    });

    const applyBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Apply');
    applyBtn.addEventListener('click', () => {
      const checked = overlay.querySelector<HTMLInputElement>('input[name="vid-shareback"]:checked');
      // If the chosen profile is the default Public, omit the id (server default).
      const chosen = checked?.value;
      const isDefault = preselect && chosen === preselect.id && preselect.is_public;
      finish({ profileId: isDefault ? undefined : chosen });
    });

    const closeBtn = h('button', {
      class: 'btn btn-ghost btn-sm', 'aria-label': 'Close',
      style: 'position:absolute;top:10px;right:10px',
    }, '✕');
    closeBtn.addEventListener('click', () => finish({ profileId: undefined }));

    const dialog = h('div', {
      class: 'card', role: 'dialog', 'aria-modal': 'true',
      style: 'position:relative;max-width:420px;width:100%;margin:0',
    },
      closeBtn,
      h('h3', { style: 'margin:0 0 4px' }, `Share a profile back with ${inviterName}`),
      h('p', { class: 'muted', style: 'margin:0 0 12px;font-size:.85rem' },
        'Pick one of your profiles to share back. Public is used if you dismiss this.'),
      h('div', {}, ...options),
      h('div', { style: 'display:flex;justify-content:flex-end;gap:8px;margin-top:12px' }, applyBtn),
    );

    const overlay = h('div', {
      style: 'position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:16px',
    }, dialog);
    // Click backdrop (outside the dialog) dismisses -> default Public.
    overlay.addEventListener('click', (e) => { if (e.target === overlay) finish({ profileId: undefined }); });

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') finish({ profileId: undefined }); };
    document.addEventListener('keydown', onKey);

    document.body.append(overlay);
  });
}
